import { AfterViewInit, Component, ElementRef, forwardRef, HostListener, Input, OnChanges, OnDestroy, Optional, Self, SimpleChanges, ViewChild } from '@angular/core';
import { AbstractControl, ControlValueAccessor, NgControl, ValidationErrors, Validator } from '@angular/forms';
import { MatFormFieldControl } from '@angular/material/form-field';
import { Subject } from 'rxjs';
import { classeBadgeVariavel, isVariavelParametro } from '@shared/components/formula/utils/formula-badge.util';
import { VariavelStruct } from '@shared/services/structs/formula/variavel.struct';

@Component({
  selector: 'app-observacao-editor',
  standalone: true,
  providers: [
    {
      provide: MatFormFieldControl,
      useExisting: forwardRef(() => ObservacaoEditorComponent),
    },
  ],
  templateUrl: './observacao-editor.component.html',
  styleUrl: './observacao-editor.component.css',
})
export class ObservacaoEditorComponent implements AfterViewInit, ControlValueAccessor, MatFormFieldControl<string>, OnChanges, OnDestroy, Validator {
  private static proximoId = 0;

  @Input() placeholder = '';
  @Input() rows = 4;
  @Input() variaveis: VariavelStruct[] = [];
  @Input() required = false;

  @ViewChild('editorRef', { static: true }) private editorRef!: ElementRef<HTMLDivElement>;

  public readonly stateChanges = new Subject<void>();
  public readonly controlType = 'observacao-editor';
  public readonly id = `observacao-editor-${ObservacaoEditorComponent.proximoId++}`;
  public describedByIds: string[] = [];
  public focused = false;

  private innerValue = '';
  private isDisabled = false;
  private variaveisSignature = '';
  private selectionRange: Range | null = null;
  private onChange: (value: string) => void = () => {};
  private onTouched: () => void = () => {};
  private onValidatorChange: () => void = () => {};
  private readonly validarControl = (control: AbstractControl): ValidationErrors | null => this.validate(control);

  constructor(@Optional() @Self() public ngControl: NgControl | null) {
    if (this.ngControl) {
      this.ngControl.valueAccessor = this;
    }
  }

  public get value(): string {
    return this.innerValue;
  }

  public set value(value: string | null) {
    const normalizedValue = value ?? '';
    if (normalizedValue === this.innerValue) return;

    this.innerValue = normalizedValue;
    this.updateEditorHtmlFromValue();
    this.stateChanges.next();
  }

  @Input()
  public get disabled(): boolean {
    return this.isDisabled;
  }

  public set disabled(value: boolean) {
    this.isDisabled = value;
    this.stateChanges.next();
  }

  public get empty(): boolean {
    return !this.innerValue;
  }

  public get shouldLabelFloat(): boolean {
    return this.focused || !this.empty;
  }

  public get errorState(): boolean {
    return !!this.ngControl?.invalid && (!!this.ngControl.touched || !!this.ngControl.dirty);
  }

  public ngAfterViewInit(): void {
    this.updateEditorHtmlFromValue();
    this.ngControl?.control?.addValidators(this.validarControl);
    this.ngControl?.control?.updateValueAndValidity({ emitEvent: false });
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes['variaveis']) {
      const signature = JSON.stringify(this.variaveis.map(variavel => [
        this.variavelChave(variavel),
        variavel.nome,
        variavel.descricao,
        variavel.observacao,
        variavel.pathTagXml,
      ]));

      if (signature !== this.variaveisSignature) {
        this.variaveisSignature = signature;
        this.updateEditorHtmlFromValue();
        this.onValidatorChange();
        this.ngControl?.control?.updateValueAndValidity({ emitEvent: false });
      }
    }
    this.stateChanges.next();
  }

  public ngOnDestroy(): void {
    this.ngControl?.control?.removeValidators(this.validarControl);
    this.stateChanges.complete();
  }

  public writeValue(value: unknown): void {
    this.value = value == null ? '' : String(value);
  }

  public registerOnChange(fn: (value: string) => void): void {
    this.onChange = fn;
  }

  public registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  public registerOnValidatorChange(fn: () => void): void {
    this.onValidatorChange = fn;
  }

  /** Rejeita referências pendentes, desconhecidas ou com delimitadores incompletos. */
  public validate(control: AbstractControl): ValidationErrors | null {
    const valor = control.value == null ? '' : String(control.value);
    if (!valor) return null;

    const chavesDisponiveis = new Set(this.variaveis.map(variavel => this.variavelChave(variavel)));
    const referencias = [...valor.matchAll(/\{([^{}]*)\}/g)];
    const possuiPendente = referencias.some(referencia => !referencia[1]);
    const possuiDesconhecida = referencias.some(referencia => referencia[1] && !chavesDisponiveis.has(referencia[1]));
    const textoSemReferencias = valor.replace(/\{[^{}]*\}/g, '');
    const possuiDelimitadorIncompleto = textoSemReferencias.includes('{') || textoSemReferencias.includes('}');

    return possuiPendente || possuiDesconhecida || possuiDelimitadorIncompleto
      ? { observacaoVariavelInvalida: true }
      : null;
  }

  public setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  public setDescribedByIds(ids: string[]): void {
    this.describedByIds = ids;
  }

  public onContainerClick(): void {
    if (!this.disabled) {
      this.editorRef.nativeElement.focus();
    }
  }

  public onFocus(): void {
    this.focused = true;
    this.stateChanges.next();
  }

  public onBlur(): void {
    this.saveSelection();
    this.focused = false;
    this.onTouched();
    this.stateChanges.next();
  }

  public onInput(): void {
    this.innerValue = this.serializeFromHtml();
    this.onChange(this.innerValue);
    this.saveSelection();
    this.stateChanges.next();
  }

  public onPaste(event: ClipboardEvent): void {
    event.preventDefault();
    const text = event.clipboardData?.getData('text/plain') ?? '';
    if (!text) return;

    const range = this.getInsertionRange();
    range.deleteContents();

    const textNode = document.createTextNode(text);
    range.insertNode(textNode);
    range.setStartAfter(textNode);
    range.collapse(true);
    this.restoreSelection(range);
    this.onInput();
  }

  public onKeydown(event: KeyboardEvent): void {
    if (event.key !== 'Backspace' && event.key !== 'Delete') return;

    const selection = window.getSelection();
    if (!selection?.rangeCount) return;

    const range = selection.getRangeAt(0);
    if (!range.collapsed || !this.editorRef.nativeElement.contains(range.commonAncestorContainer)) return;

    const variavelElement = this.findAdjacentVariavel(range, event.key === 'Backspace');
    if (!variavelElement) return;

    event.preventDefault();
    const nextRange = document.createRange();
    nextRange.setStartBefore(variavelElement);
    nextRange.collapse(true);
    variavelElement.remove();
    this.restoreSelection(nextRange);
    this.onInput();
  }

  public saveSelection(): void {
    const selection = window.getSelection();
    if (!selection?.rangeCount) return;

    const range = selection.getRangeAt(0);
    if (this.editorRef.nativeElement.contains(range.commonAncestorContainer)) {
      this.selectionRange = range.cloneRange();
    }
  }

  @HostListener('document:selectionchange')
  public onSelectionChange(): void {
    if (this.focused) {
      this.saveSelection();
    }
  }

  public inserirVariavel(variavel: VariavelStruct): void {
    const chave = this.variavelChave(variavel);
    const span = this.createVariavelElement(chave, variavel);
    const range = this.getInsertionRange();

    range.deleteContents();
    range.insertNode(span);
    range.setStartAfter(span);
    range.collapse(true);

    this.editorRef.nativeElement.focus();
    this.restoreSelection(range);
    this.onInput();
  }

  private updateEditorHtmlFromValue(): void {
    if (!this.editorRef) return;

    const editor = this.editorRef.nativeElement;
    editor.replaceChildren();

    const regex = /\{([^{}]*)\}/g;
    let lastIndex = 0;
    let match: RegExpExecArray | null;

    while ((match = regex.exec(this.innerValue)) !== null) {
      if (match.index > lastIndex) {
        editor.appendChild(document.createTextNode(this.innerValue.slice(lastIndex, match.index)));
      }

      const chave = match[1];
      const variavel = this.variaveis.find(item => this.variavelChave(item) === chave);
      editor.appendChild(this.createVariavelElement(chave, variavel));
      lastIndex = regex.lastIndex;
    }

    if (lastIndex < this.innerValue.length) {
      editor.appendChild(document.createTextNode(this.innerValue.slice(lastIndex)));
    }
  }

  private createVariavelElement(chave: string, variavel?: VariavelStruct): HTMLSpanElement {
    const span = document.createElement('span');
    const situacaoClass = !chave
      ? 'observacao-variavel--pendente'
      : !variavel ? 'observacao-variavel--invalida' : '';
    span.className = `status-badge ${classeBadgeVariavel(variavel)} badge-variavel observacao-variavel ${situacaoClass}`.trim();
    span.contentEditable = 'false';
    span.setAttribute('data-chave', chave);
    span.title = !chave
      ? 'Referência pendente. Substitua ou remova este marcador antes de salvar.'
      : !variavel
        ? `Variável indisponível neste contexto: ${chave}`
        : [variavel.observacao || variavel.descricao, `Chave: ${chave}`, variavel.pathTagXml ? `Path XML: ${variavel.pathTagXml}` : null]
            .filter(Boolean)
            .join('\n');

    const descricao = document.createElement('span');
    descricao.className = 'variavel-badge-descricao';
    descricao.textContent = !chave
      ? 'Referência pendente'
      : variavel?.descricao ?? 'Variável indisponível';

    const nome = document.createElement('span');
    nome.className = 'variavel-badge-nome';
    nome.textContent = !chave ? '{}' : `{${variavel?.nome || chave}}`;

    if (isVariavelParametro(variavel)) {
      const icone = document.createElement('span');
      icone.className = 'material-symbols-outlined';
      icone.textContent = 'settings';
      icone.setAttribute('aria-label', 'Parâmetro de configuração');
      icone.title = 'Parâmetro de configuração';
      span.append(icone);
    }

    if (variavel?.possuiFormulaAplicavel) {
      const icone = document.createElement('span');
      icone.className = 'material-symbols-outlined';
      icone.textContent = 'function';
      icone.setAttribute('aria-label', 'Possui fórmula aplicável');
      icone.title = 'Possui fórmula aplicável';
      span.append(icone);
    }
    span.append(descricao, nome);
    return span;
  }

  private serializeFromHtml(): string {
    let result = '';

    const walk = (node: Node): void => {
      if (node.nodeType === Node.TEXT_NODE) {
        result += node.textContent ?? '';
        return;
      }

      if (node.nodeType !== Node.ELEMENT_NODE) return;

      const element = node as HTMLElement;
      if (element.classList.contains('observacao-variavel') && element.hasAttribute('data-chave')) {
        result += `{${element.dataset['chave'] ?? ''}}`;
        return;
      }

      if (element.tagName === 'BR') {
        result += '\n';
        return;
      }

      const isBlock = element.tagName === 'DIV' || element.tagName === 'P';
      if (isBlock && result && !result.endsWith('\n')) {
        result += '\n';
      }

      const isEmptyBlock = isBlock && element.childNodes.length === 1 && element.firstChild instanceof HTMLBRElement;
      if (!isEmptyBlock) {
        element.childNodes.forEach(walk);
      }
    };

    this.editorRef.nativeElement.childNodes.forEach(walk);
    return result;
  }

  private findAdjacentVariavel(range: Range, backwards: boolean): HTMLElement | null {
    const editor = this.editorRef.nativeElement;
    let container = range.startContainer;
    let offset = range.startOffset;

    if (container.nodeType === Node.TEXT_NODE) {
      const textLength = container.textContent?.length ?? 0;
      if ((backwards && offset > 0) || (!backwards && offset < textLength)) return null;

      offset = Array.prototype.indexOf.call(container.parentNode?.childNodes ?? [], container);
      if (!backwards) offset++;
      container = container.parentNode ?? container;
    }

    while (container !== editor) {
      const element = container as Element;
      const candidate = backwards
        ? element.childNodes[offset - 1]
        : element.childNodes[offset];

      if (candidate) {
        return candidate.nodeType === Node.ELEMENT_NODE && (candidate as HTMLElement).classList.contains('observacao-variavel')
          ? candidate as HTMLElement
          : null;
      }

      const parent = container.parentNode;
      if (!parent) return null;

      offset = Array.prototype.indexOf.call(parent.childNodes, container);
      if (!backwards) offset++;
      container = parent;
    }

    const candidate = backwards
      ? editor.childNodes[offset - 1]
      : editor.childNodes[offset];

    return candidate?.nodeType === Node.ELEMENT_NODE && (candidate as HTMLElement).classList.contains('observacao-variavel')
      ? candidate as HTMLElement
      : null;
  }

  private getInsertionRange(): Range {
    const editor = this.editorRef.nativeElement;
    if (this.selectionRange && editor.contains(this.selectionRange.commonAncestorContainer)) {
      return this.selectionRange.cloneRange();
    }

    const range = document.createRange();
    range.selectNodeContents(editor);
    range.collapse(false);
    return range;
  }

  private restoreSelection(range: Range): void {
    const selection = window.getSelection();
    selection?.removeAllRanges();
    selection?.addRange(range);
    this.selectionRange = range.cloneRange();
  }

  private variavelChave(variavel: VariavelStruct): string {
    return variavel.chave || variavel.tagXml || variavel.nome;
  }
}
